import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Chip,
  Collapse,
  Divider,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Send,
  Paperclip,
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';

/**
 * EmailComposer - Rich email composition component
 * Adapted from Mail-0/Zero for GatewayX
 */
const EmailComposer = ({
  initialTo = [],
  initialCc = [],
  initialBcc = [],
  initialSubject = '',
  initialMessage = '',
  initialAttachments = [],
  onSend,
  onClose,
  onSaveDraft,
  sending = false,
  replyingTo = null,
  mode = 'compose', // 'compose' | 'reply' | 'replyAll' | 'forward'
  compact = false,
  autoFocus = true,
}) => {
  // Recipients state
  const [to, setTo] = useState(initialTo);
  const [cc, setCc] = useState(initialCc);
  const [bcc, setBcc] = useState(initialBcc);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  
  // Content state
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [attachments, setAttachments] = useState(initialAttachments);
  
  // UI state
  const [showCc, setShowCc] = useState(initialCc.length > 0);
  const [showBcc, setShowBcc] = useState(initialBcc.length > 0);
  const [isExpanded, setIsExpanded] = useState(!compact);
  
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  // Note: Initial values are set via useState defaults
  // If you need to reset when props change, the parent should use a key prop

  // Auto focus message input for replies
  useEffect(() => {
    if (autoFocus && messageInputRef.current && mode !== 'compose') {
      setTimeout(() => messageInputRef.current?.focus(), 100);
    }
  }, [autoFocus, mode]);

  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Add recipient
  const addRecipient = (type, email) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) return false;

    const setters = { to: setTo, cc: setCc, bcc: setBcc };
    const current = { to, cc, bcc }[type];

    if (!current.includes(trimmedEmail)) {
      setters[type]([...current, trimmedEmail]);
    }
    return true;
  };

  // Remove recipient
  const removeRecipient = (type, email) => {
    const setters = { to: setTo, cc: setCc, bcc: setBcc };
    const current = { to, cc, bcc }[type];
    setters[type](current.filter(e => e !== email));
  };

  // Handle recipient input keydown
  const handleRecipientKeyDown = (type, input, setInput) => (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      if (addRecipient(type, input)) {
        setInput('');
      }
    } else if (e.key === 'Backspace' && !input) {
      const current = { to, cc, bcc }[type];
      if (current.length > 0) {
        removeRecipient(type, current[current.length - 1]);
      }
    }
  };

  // Handle recipient input blur
  const handleRecipientBlur = (type, input, setInput) => () => {
    if (input.trim()) {
      if (addRecipient(type, input)) {
        setInput('');
      }
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Handle send
  const handleSend = useCallback(async () => {
    if (to.length === 0 || !subject.trim() || !message.trim()) {
      return;
    }

    if (onSend) {
      await onSend({
        to,
        cc: cc.length > 0 ? cc : undefined,
        bcc: bcc.length > 0 ? bcc : undefined,
        subject,
        message,
        attachments,
      });
    }
  }, [to, cc, bcc, subject, message, attachments, onSend]);

  // Render recipient input
  const renderRecipientInput = (label, recipients, input, setInput, type, show = true) => {
    if (!show) return null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <Typography
          variant="body2"
          sx={{ 
            minWidth: 40, 
            pt: 1, 
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {label}:
        </Typography>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            p: 0.5,
            minHeight: 40,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            '&:focus-within': {
              borderColor: 'primary.main',
            },
          }}
        >
          {recipients.map((email) => (
            <Chip
              key={email}
              label={email}
              size="small"
              onDelete={() => removeRecipient(type, email)}
              sx={{ maxWidth: 200 }}
            />
          ))}
          <TextField
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleRecipientKeyDown(type, input, setInput)}
            onBlur={handleRecipientBlur(type, input, setInput)}
            placeholder={recipients.length === 0 ? 'Add recipients...' : ''}
            variant="standard"
            size="small"
            sx={{
              flex: 1,
              minWidth: 150,
              '& .MuiInput-root': {
                '&:before, &:after': { display: 'none' },
              },
              '& .MuiInputBase-input': {
                p: 0.5,
              },
            }}
            InputProps={{ disableUnderline: true }}
          />
        </Box>
      </Box>
    );
  };

  return (
    <Paper
      elevation={compact ? 0 : 3}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: compact ? '1px solid' : 'none',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {/* Header */}
      {!compact && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            bgcolor: 'grey.100',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {mode === 'compose' && 'New Message'}
            {mode === 'reply' && 'Reply'}
            {mode === 'replyAll' && 'Reply All'}
            {mode === 'forward' && 'Forward'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </IconButton>
            {onClose && (
              <IconButton size="small" onClick={onClose}>
                <X size={18} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <Collapse in={isExpanded || compact}>
        <Box sx={{ p: 2 }}>
          {/* Recipients */}
          {renderRecipientInput('To', to, toInput, setToInput, 'to')}
          
          {/* Cc/Bcc toggle */}
          {!showCc && !showBcc && (
            <Box sx={{ display: 'flex', gap: 1, mb: 1, ml: 6 }}>
              <Button size="small" onClick={() => setShowCc(true)}>Cc</Button>
              <Button size="small" onClick={() => setShowBcc(true)}>Bcc</Button>
            </Box>
          )}
          
          {renderRecipientInput('Cc', cc, ccInput, setCcInput, 'cc', showCc)}
          {renderRecipientInput('Bcc', bcc, bccInput, setBccInput, 'bcc', showBcc)}

          {/* Subject */}
          <TextField
            fullWidth
            placeholder="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                    Subject:
                  </Typography>
                </InputAdornment>
              ),
            }}
          />

          {/* Message body - Rich Text Editor */}
          <Box
            sx={{
              mb: 2,
              '& .quill': {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:focus-within': {
                  borderColor: 'primary.main',
                },
              },
              '& .ql-toolbar': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                backgroundColor: 'grey.50',
              },
              '& .ql-container': {
                minHeight: compact ? 100 : 200,
                maxHeight: 400,
                overflow: 'auto',
                fontSize: '14px',
                fontFamily: 'inherit',
                borderBottomLeftRadius: 4,
                borderBottomRightRadius: 4,
              },
              '& .ql-editor': {
                minHeight: compact ? 100 : 200,
              },
              '& .ql-editor.ql-blank::before': {
                fontStyle: 'normal',
                color: 'text.disabled',
              },
            }}
          >
            <ReactQuill
              theme="snow"
              value={message}
              onChange={setMessage}
              placeholder="Write your message..."
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  ['link', 'blockquote'],
                  ['clean']
                ],
              }}
              formats={[
                'header', 'bold', 'italic', 'underline', 'strike',
                'list', 'bullet', 'link', 'blockquote'
              ]}
            />
          </Box>

          {/* Attachments */}
          {attachments.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Attachments ({attachments.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {attachments.map((file, index) => (
                  <Chip
                    key={index}
                    label={`${file.name} (${formatFileSize(file.size)})`}
                    onDelete={() => removeAttachment(index)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={handleFileSelect}
              />
              <Tooltip title="Attach files">
                <IconButton 
                  size="small" 
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip size={18} />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              {onClose && (
                <Button
                  variant="text"
                  color="inherit"
                  onClick={onClose}
                  disabled={sending}
                >
                  Discard
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSend}
                disabled={sending || to.length === 0 || !subject.trim()}
                startIcon={sending ? <CircularProgress size={16} /> : <Send size={16} />}
              >
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

EmailComposer.propTypes = {
  initialTo: PropTypes.arrayOf(PropTypes.string),
  initialCc: PropTypes.arrayOf(PropTypes.string),
  initialBcc: PropTypes.arrayOf(PropTypes.string),
  initialSubject: PropTypes.string,
  initialMessage: PropTypes.string,
  initialAttachments: PropTypes.array,
  onSend: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onSaveDraft: PropTypes.func,
  sending: PropTypes.bool,
  replyingTo: PropTypes.string,
  mode: PropTypes.oneOf(['compose', 'reply', 'replyAll', 'forward']),
  compact: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export default EmailComposer;
